/* eslint-disable no-unused-vars */
import { getAnalytics } from 'firebase/analytics';
import { onAuthStateChanged } from 'firebase/auth';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { signInWithCustomToken } from 'firebase/auth';
/* eslint-enable no-unused-vars */

import React, { useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore, collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';
import DataTable from './components/DataTable';
import StarRating from './components/StarRating';
import AddressSearch from './components/AddressSearch';
import RentMap from './components/RentMap';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Add these console logs right before initializing Firebase
console.log('Firebase Config:', firebaseConfig);
console.log('Checking env vars:', {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  // ... log other env vars
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);
console.log('Firebase app initialized:', app);

const auth = getAuth(app);
console.log('Firebase auth initialized:', auth);

const db = getFirestore(app);
console.log('DB type:', typeof db, 'DB value:', db);
// eslint-disable-next-line no-unused-vars
const analytics = getAnalytics(app);

// At the top level of App.js, add these animation variants
const pageVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

const pageTransition = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1] // Material Design standard easing
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState('form');
  const [formData, setFormData] = useState({
    email: '',
    address: '',
    currentRent: '',
    initialRent: '',
    utilities: '',
    utilitiesNotIncluded: [],
    moveInMonth: '',
    moveInYear: '',
    borough: '',
    bedrooms: '',
    unitsInBuilding: '',
    occupants: '',
    nonStandardBedrooms: '',
    landlordName: '',
    additionalComments: '',
    landlordRating: 0,
  });

  const totalSteps = 11;
  const motionProps = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1] // Material Design standard easing
    },
    className: "w-full"
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const NavigationButtons = ({ onPrev, onNext }) => (
    <div className="flex justify-between mt-4">
      {onPrev && (
        <button
          onClick={onPrev}
          className="w-[48%] bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400"
        >
          Back
        </button>
      )}
      <button
        onClick={onNext}
        disabled={isSubmitting}
        className={`${onPrev ? 'w-[48%]' : 'w-full'} bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed`}
      >
        {step === totalSteps ? (isSubmitting ? 'Submitting...' : 'Submit') : 'Next'}
      </button>
    </div>
  );

  const handleSubmit = async () => {
    if (isDemoMode) {
      alert('Demo Mode: In a real submission, this data would be saved to our database.');
      setStep(1);  // Reset form
      return;
    }

    // Validate all required steps before submission
    const requiredSteps = [1, 3, 7, 8, 12];
    for (let step of requiredSteps) {
      const error = validateStep(step);
      if (error) {
        alert(error);
        setStep(step);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const db = getFirestore();
      
      if (formData.id) {
        const submissionRef = doc(db, 'submissions', formData.id);
        await updateDoc(submissionRef, {
          ...formData,
          updatedAt: new Date().toISOString()
        });
      } else {
        await addDoc(collection(db, 'submissions'), {
          ...formData,
          userId: auth.currentUser.uid,
          submittedAt: new Date().toISOString()
        });
      }

      alert(formData.id ? 'Submission updated!' : 'Thank you for your submission!');
      setFormData({
        email: '',
        address: '',
        currentRent: '',
        // ... reset all other fields ...
      });
      setStep(1);
      setView('data');

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1: // Email
        if (!formData.email) {
          return 'Please enter your email address';
        }
        break;
      
      case 3: // Address
        if (!formData.address) {
          return 'Please enter the street address';
        }
        break;

      case 7: // Borough
        if (!formData.borough) {
          return 'Please select a borough';
        }
        break;

      case 8: // Bedrooms
        if (!formData.bedrooms) {
          return 'Please enter the number of bedrooms';
        }
        break;

      case 12: // Landlord
        if (!formData.landlordName) {
          return 'Please enter the landlord or management company name';
        }
        break;

      default:
        return null;
    }
  };

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1: // Email
        return (
          <motion.div key="step1" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                readOnly
                className="w-full px-3 py-2 border rounded-md bg-gray-100"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                This email is associated with your account
              </p>
            </div>
            <NavigationButtons onNext={nextStep} />
          </motion.div>
        );

      case 2: // Address Details
        return (
          <motion.div key="step2" {...motionProps}>
            <div className="space-y-4">
              <AddressSearch 
                onAddressSelect={(addressData) => {
                  setFormData(prev => ({
                    ...prev,
                    address: addressData.fullAddress,
                    borough: addressData.borough,
                    // Store coordinates for future use
                    latitude: addressData.lat,
                    longitude: addressData.lng
                  }));
                }}
              />
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 3: // Number of bedrooms
        return (
          <motion.div key="step3" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                Number of Bedrooms <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.bedrooms}
                onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                className="w-full px-3 pr-16 py-2 border rounded-md"
                required
              >
                <option value="">Select number of bedrooms</option>
                <option value="0">Studio</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5+</option>
              </select>
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 4: // Number of occupants
        return (
          <motion.div key="step4" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                How many people live in your apartment?
              </label>
              <input
                type="number"
                min="1"
                value={formData.occupants}
                onChange={(e) => handleInputChange('occupants', e.target.value)}
                className="w-full px-3 py-2 border rounded-md [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 5: // Move-in date
        return (
          <motion.div key="step5" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                When did you move in?
              </label>
              <div className="flex gap-4">
                <div className="w-1/2">
                  <select
                    value={formData.moveInMonth}
                    onChange={(e) => handleInputChange('moveInMonth', e.target.value)}
                    className="w-full px-3 pr-16 py-2 border rounded-md"
                  >
                    <option value="">Month</option>
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div className="w-1/2">
                  <select
                    value={formData.moveInYear}
                    onChange={(e) => handleInputChange('moveInYear', e.target.value)}
                    className="w-full px-3 pr-16 py-2 border rounded-md"
                  >
                    <option value="">Year</option>
                    {Array.from(
                      { length: new Date().getFullYear() - 1959 },
                      (_, i) => new Date().getFullYear() - i
                    ).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 6: // Current monthly rent
        return (
          <motion.div key="step6" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                Current Monthly Rent <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-600">$</span>
                <input
                  type="number"
                  value={formData.currentRent}
                  onChange={(e) => handleInputChange('currentRent', e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border rounded-md [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  required
                />
              </div>
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 7: // Initial monthly rent
        return (
          <motion.div key="step7" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                Initial Monthly Rent
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-600">$</span>
                <input
                  type="number"
                  value={formData.initialRent}
                  onChange={(e) => handleInputChange('initialRent', e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border rounded-md [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 8: // Utilities
        return (
          <motion.div key="step8" {...motionProps}>
            <div className="space-y-4">
              <div>
                <label className="block text-lg font-normal text-gray-600">
                  Average Monthly Utilities Cost
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-600">$</span>
                  <input
                    type="number"
                    value={formData.utilities}
                    onChange={(e) => handleInputChange('utilities', e.target.value)}
                    className="w-full pl-7 pr-3 py-2 border rounded-md [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-lg font-normal text-gray-600 mb-2">
                  Which utilities are NOT included in rent?
                </label>
                {['Electric', 'Gas', 'Heat', 'Hot Water'].map((utility) => (
                  <div key={utility} className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id={utility.toLowerCase()}
                      checked={formData.utilitiesNotIncluded?.includes(utility.toLowerCase())}
                      onChange={(e) => {
                        const value = utility.toLowerCase();
                        setFormData(prev => ({
                          ...prev,
                          utilitiesNotIncluded: e.target.checked
                            ? [...(prev.utilitiesNotIncluded || []), value]
                            : (prev.utilitiesNotIncluded || []).filter(item => item !== value)
                        }));
                      }}
                      className="h-4 w-4 text-blue-500 rounded border-gray-300"
                    />
                    <label htmlFor={utility.toLowerCase()} className="ml-2">
                      {utility}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 9: // Units in building
        return (
          <motion.div key="step9" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                Approximately how many units are in your building?
              </label>
              <input
                type="number"
                min="1"
                value={formData.unitsInBuilding}
                onChange={(e) => handleInputChange('unitsInBuilding', e.target.value)}
                className="w-full px-3 py-2 border rounded-md [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 10: // Landlord/Management with rating
        return (
          <motion.div key="step10" {...motionProps}>
            <div className="space-y-4">
              <div>
                <label className="block text-lg font-normal text-gray-600">
                  Landlord or Management Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.landlordName}
                  onChange={(e) => handleInputChange('landlordName', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-normal text-gray-600 mb-2">
                  Rate your experience with this landlord/management
                </label>
                <StarRating
                  rating={formData.landlordRating}
                  onRatingChange={(value) => handleInputChange('landlordRating', value)}
                />
              </div>
            </div>
            <NavigationButtons onPrev={prevStep} onNext={nextStep} />
          </motion.div>
        );

      case 11: // Additional comments
        return (
          <motion.div key="step11" {...motionProps}>
            <div className="mb-6">
              <label className="block text-lg font-normal text-gray-600">
                Anything you'd like to add?
              </label>
              <textarea
                value={formData.additionalComments}
                onChange={(e) => handleInputChange('additionalComments', e.target.value)}
                className="w-full px-3 py-2 border rounded-md h-32"
                placeholder="Optional comments about your rental experience..."
              />
            </div>
            <NavigationButtons onPrev={prevStep} onNext={handleSubmit} />
          </motion.div>
        );

      default:
        return null;
    }
  };

  // Add useEffect to handle auth state changes
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);
        setFormData(prev => ({
          ...prev,
          email: user.email
        }));
      } else {
        setIsLoggedIn(false);
        setFormData(prev => ({
          ...prev,
          email: ''
        }));
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Update keyboard navigation to only work when logged in
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if ((!isLoggedIn && !isDemoMode) || view !== 'form') return;
      
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        if (step < totalSteps) {
          setStep(step + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (step > 1) {
          setStep(step - 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [step, view, isLoggedIn, totalSteps]);

  // Add Google Sign In functionality
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        // Force account selection even when one account is available
        prompt: 'select_account',
        // Specify origin
        auth_url: window.location.origin
      });
      
      console.log('Starting Google sign in...');
      const result = await signInWithPopup(auth, provider);
      console.log('Sign in successful', result.user);
      setIsLoggedIn(true);
    } catch (error) {
      console.error('Error signing in with Google:', error.code, error.message);
      if (error.code === 'auth/popup-closed-by-user') {
        alert('Sign-in was cancelled. Please try again.');
      } else {
        alert(`Error signing in: ${error.message}`);
      }
    }
  };

  // Add Sign Out functionality
  const signOut = async () => {
    try {
      await auth.signOut();
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Near the top where you have your state
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Replace your existing demo login useEffect with this
  React.useEffect(() => {
    const handleDemoMode = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('demo') === 'true') {
        setIsDemoMode(true);
        setIsLoggedIn(true); // Skip authentication
        setView('form');     // Show the form immediately
        setFormData(prev => ({
          ...prev,
          email: 'demo@example.com'
        }));
      }
    };

    handleDemoMode();
  }, []); // Run once on mount

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleDragEnd = (event, info) => {
    if (info.offset.x > 100 && step > 1) {
      setStep(step - 1);
    } else if (info.offset.x < -100 && step < totalSteps) {
      setStep(step + 1);
    }
  };

  return (
    <div className="bg-gray-100">
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 
                  onClick={() => setView('form')} 
                  className="text-base sm:text-xl font-bold cursor-pointer"
                >
                  NYC Rent Tracker {isDemoMode && <span className="text-yellow-600">(Demo Mode)</span>}
                </h1>
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              {(isLoggedIn || isDemoMode) && (
                <button 
                  onClick={() => setView('form')}
                  className={`text-lg ${view === 'form' ? 'text-gray-900' : 'text-gray-600'} hover:text-gray-900`}
                >
                  Submit Data
                </button>
              )}
              <button 
                onClick={() => setView('table')}
                className={`text-lg ${view === 'table' ? 'text-gray-900' : 'text-gray-600'} hover:text-gray-900`}
              >
                View All Data
              </button>
              <button 
                onClick={() => setView('map')}
                className={`text-lg ${view === 'map' ? 'text-gray-900' : 'text-gray-600'} hover:text-gray-900`}
              >
                View Map
              </button>
              {!isDemoMode && (
                isLoggedIn ? (
                  <button onClick={signOut} className="text-lg bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600">
                    Sign Out
                  </button>
                ) : (
                  <button onClick={signInWithGoogle} className="text-lg bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600">
                    Sign In
                  </button>
                )
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <span className="sr-only">Open main menu</span>
                {/* Hamburger Icon */}
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                {/* Close Icon */}
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden`}>
          <div className="px-2 pt-2 pb-3 space-y-1">
            {(isLoggedIn || isDemoMode) && (
              <button 
                onClick={() => {
                  setView('form');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              >
                Submit Data
              </button>
            )}
            <button 
              onClick={() => {
                setView('table');
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
            >
              View All Data
            </button>
            <button 
              onClick={() => {
                setView('map');
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
            >
              View Map
            </button>
            {!isDemoMode && (
              isLoggedIn ? (
                <button
                  onClick={() => {
                    signOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:text-red-700 hover:bg-gray-50"
                >
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => {
                    signInWithGoogle();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:text-red-700 hover:bg-gray-50"
                >
                  Sign In
                </button>
              )
            )}
          </div>
        </div>
      </nav>

      {/* Welcome screen */}
      {!isLoggedIn && !isDemoMode && view !== 'table' && view !== 'map' ? (
        <div className="max-w-md mx-auto px-4 flex flex-col justify-center min-h-[calc(100vh-4rem)]">
          <h2 className="text-2xl font-bold text-center mb-4">Welcome to NYC Rent Tracker</h2>
          <p className="text-center text-gray-600 mb-8">Please sign in to submit data</p>
          <button
            onClick={signInWithGoogle}
            className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 flex items-center justify-center"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="w-5 h-5 mr-2"
            />
            Sign in with Google
          </button>
        </div>
      ) : (
        <div className="bg-gray-100">
          {!isLoggedIn && !isDemoMode && view !== 'table' && view !== 'map' ? (
            <div className="max-w-md mx-auto pt-20">
              <h2 className="text-2xl font-bold text-center mb-4">Welcome to NYC Rent Tracker</h2>
              <p className="text-center text-gray-600 mb-8">Please sign in to submit data</p>
              <button
                onClick={signInWithGoogle}
                className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 flex items-center justify-center"
              >
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  className="w-5 h-5 mr-2"
                />
                Sign in with Google
              </button>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {view === 'form' ? (
                <div className="mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 max-w-md md:max-w-2xl mt-6">
                  <div className="mb-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(step / totalSteps) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-gray-500 mt-2 text-center">
                      Step {step} of {totalSteps}
                    </div>
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      {...pageVariants}
                      transition={pageTransition}
                      drag="x"
                      dragConstraints={{ left: 0, right: 0 }}
                      onDragEnd={handleDragEnd}
                    >
                      {renderStep()}
                    </motion.div>
                  </AnimatePresence>
                </div>
              ) : view === 'map' ? (
                <motion.div
                  key="map"
                  {...pageVariants}
                  transition={pageTransition}
                  className="mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 max-w-7xl mt-6"
                >
                  <h2 className="text-2xl font-bold mb-4">Rent Heatmap</h2>
                  <RentMap db={db} />
                </motion.div>
              ) : (
                <DataTable
                  db={db}
                  onEdit={(submission) => {
                    setFormData(submission);
                    setView('form');
                  }}
                  isLoggedIn={isLoggedIn}
                />
              )}
            </AnimatePresence>
          )}
        </div>
      )}
    </div>
  );
}

export default App;